import { IcDiamond, IcGlobe24, IcMenu28 } from '$assets';
import { ActionSheet, Button, Image, LottieIcon, ScrollView } from '$uikit';
import {
  createRef,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import './Landing.sass';
import { classNames, getDocumentHref, importAllFromDirectory } from '$utils';
import TonLogo from '$assets/ton_logo.svg';

import { useCurrentLang, useTranslator } from '$hooks';
import { changeLanguage, getNativeLanguage } from '$translation';
import router from '$navigation/router';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';
import { useDispatch, useSelector } from 'react-redux';
import { profileActions } from '$store/profile';
import { useRoute } from 'react-router5';
import { AuthPopoverState } from '$store/models';
import { ContentPriorityType } from '$store/main/interface';
import { authSelector } from '$store/auth';
import { FEED, REF } from '$shared/constants/pages';
import { Loader } from '$shared/components';

interface ToggleItem {
  text: string;
  backgroundColor: string;
  id: string;
}

interface CardItem {
  name: string;
  iconName: string;
  title: string;
  description: string;
}

enum ContentType {
  MEDIA = 'media',
  EROTIC = 'erotic',
}

const featuresDefaultIcons = importAllFromDirectory(
  // @ts-ignore
  require.context('../../assets/iconly/default', false, /\.(png|jpe?g|svg|webp)$/),
);

const featuresAdultIcons = importAllFromDirectory(
  // @ts-ignore
  require.context('../../assets/iconly/adult', false, /\.(png|jpe?g|svg|webp)$/),
);

const Toggle = ({
  items,
  value,
  onClick,
}: {
  items: ToggleItem[];
  value: string;
  onClick: (id: string) => void;
}) => {
  const toggleRef = useRef<HTMLDivElement>(null);
  const [toggleWidth, setToggleWidth] = useState(0);
  const [toggleHandleStyle, setToggleHandleStyle] = useState<{
    maxWidth: number;
    maxHeight: number;
    background: string;
    transform: string;
  }>({ maxWidth: 0, maxHeight: 0, background: '', transform: '' });
  const [toggleItemRefs, setToggleItemRefs] = useState<
    MutableRefObject<HTMLDivElement>[]
  >([]);

  useEffect(() => {
    setToggleItemRefs((elRefs) =>
      Array(items.length)
        .fill(null)
        .map((_, i) => elRefs[i] || createRef()),
    );
  }, [items.length]);

  useEffect(() => {
    if (toggleItemRefs.length) {
      toggleItemRefs.forEach((itemRef, ind) => {
        const item = itemRef?.current;

        if (item.classList.contains('active')) {
          setToggleHandleStyle({
            maxWidth: item.offsetWidth,
            maxHeight: item.offsetHeight,
            background: items[item?.id].backgroundColor,
            transform: `translateX(${
              ind > 0 ? getItemsWidth(toggleItemRefs, ind) : 0
            }px)`,
          });
        }
      });
    }
  }, [value, toggleItemRefs, toggleWidth]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      setToggleWidth(entries[0].contentRect.width);
    });
    toggleRef.current && observer.observe(toggleRef.current);
    return () => {
      toggleRef.current && observer.unobserve(toggleRef.current);
    };
  }, []);

  const getItemsWidth = (items, endInd) => {
    return items
      .slice(0, endInd)
      .reduce((width, item) => width + (item.current?.offsetWidth || 0), 0);
  };

  return (
    <div className="Toggle" ref={toggleRef}>
      <div className="Toggle__handle" style={{ ...toggleHandleStyle }} />
      {items.map((item, ind) => {
        const { text, id } = item;
        return (
          <div
            ref={toggleItemRefs[ind]}
            key={id}
            id={ind.toString()}
            onClick={() => onClick(id)}
            className={classNames('Toggle__item', {
              active: id === value,
            })}
          >
            {text}
          </div>
        );
      })}
    </div>
  );
};

const FeatureCards = ({
  cards,
  contentType,
}: {
  cards: CardItem[];
  contentType: ContentType;
}) => {
  return (
    <>
      {cards.map((card) => {
        const { name, description, iconName, title } = card;
        let type = 'default';
        if (contentType === ContentType.EROTIC) {
          type = 'adult';
        }
        return (
          <div className={`Landing__card _feature _${name}`} key={name}>
            <Image
              src={require(`./../../assets/iconly/default/wallet.png`).default}
              webp={require(`./../../assets/iconly/${type}/${iconName}.webp`).default}
              alt={name}
            />
            <div className="Landing__card_content">
              <h4>{title}</h4>
              <p>{description}</p>
            </div>
          </div>
        );
      })}
    </>
  );
};

export const Landing = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { route } = useRoute();
  const currentLang = useCurrentLang();
  const { isLogged } = useSelector(authSelector);
  const [contentType, setContentType] = useState(
    window.isMedia ? ContentType.MEDIA : route.params.c || ContentType.EROTIC,
  );
  const [isActionSheetOpened, setIsActionSheetOpened] = useState(false);
  const [isVisible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    if (route.params.c === ContentType.EROTIC) {
      window.localStorage.setItem('contentPriorityType', ContentPriorityType.ADULT);
    }
  }, []);

  useEffect(() => {
    if (route.name === REF && isLogged) {
      router.navigate(FEED);
    } else {
      setVisible(true);
    }
  }, [isLogged]);

  const handleChangeContentType = useCallback((type: ContentType) => {
    setContentType(type);
    router.navigate(
      route.name,
      {
        c: type === ContentType.EROTIC ? 'erotic' : 'media',
      },
      { replace: true },
    );
  }, []);

  const toggleItems = [
    {
      text: t('landing_toggle_erotic'),
      id: ContentType.EROTIC,
      backgroundColor: 'linear-gradient(135deg, #AA61F2 0%, #6950E6 100%)',
    },
    {
      text: t('landing_toggle_media'),
      id: ContentType.MEDIA,
      backgroundColor: 'linear-gradient(135deg, #2EC7E6 0%, #2E8AE6 100%)',
    },
  ];

  const featureCards = [
    {
      name: 'payouts',
      title: t('landing_feature_title_payouts'),
      description: t('landing_feature_description_payouts'),
      iconName: 'heart',
    },
    {
      name: 'messages',
      title: t('landing_feature_title_messages'),
      description: t('landing_feature_description_messages'),
      iconName: 'message',
    },
    {
      name: 'membership',
      title: t('landing_feature_title_membership'),
      description: t('landing_feature_description_membership'),
      iconName: 'star',
    },
    {
      name: 'tips',
      title: t('landing_feature_title_tips'),
      description: t('landing_feature_description_tips'),
      iconName: 'wallet',
    },
  ];

  const toggleLanguage = () => {
    const nativeLanguage = getNativeLanguage();
    const secondLanguage = nativeLanguage === 'en' ? 'ru' : nativeLanguage;
    changeLanguage(currentLang.key === 'en' ? secondLanguage : 'en');
  };

  const openAuthPopover = (authType: AuthPopoverState) => {
    dispatch(profileActions.setAuthPopoverState(authType));
  };

  const actions = [
    // { label: 'About', value: 'goToAbout' },
    {
      label: '',
      value: 'signIn',
      children: <Button mode="quinary">{t('landing_login')}</Button>,
    },
    {
      label: '',
      value: 'signUp',
      children: <Button>{t('landing_join')}</Button>,
    },
    // {
    //   label: '',
    //   value: 'changeLanguage',
    //   children: (
    //     <div className="Landing__changeLanguage_btn _actionSheet">
    //       <IcGlobe24 /> {currentLang.key}
    //     </div>
    //   ),
    // },
  ];

  const handleActionClick = (value) => {
    setIsActionSheetOpened(false);

    switch (value) {
      case 'signIn':
        openAuthPopover(AuthPopoverState.LOGIN);
        break;
      case 'signUp':
        openAuthPopover(AuthPopoverState.REGISTER);
        break;
      case 'changeLanguage':
        toggleLanguage();
    }
  };

  if (!isVisible) return <Loader isWrapped />;

  return (
    <ScrollView className={`Landing _${currentLang.key}`}>
      <div className="Landing__wrapper __header">
        <div className="Landing__header Landing__content">
          <div className="Landing__logo">
            <IcDiamond />
            <span>{window.isMedia ? 'TonPlace.io' : 'Ton.Place'}</span>
          </div>
          <div className="Landing__header__content">
            <div className="Landing__header__btns">
              <Button onClick={() => openAuthPopover(AuthPopoverState.REGISTER)}>
                {t('landing_join')}
              </Button>
              <Button
                mode="quinary"
                onClick={() => openAuthPopover(AuthPopoverState.LOGIN)}
              >
                {t('landing_login')}
              </Button>
            </div>
            {/*<ul className="Landing__nav">*/}
            {/*  <li className="Landing__changeLanguage_btn" onClick={toggleLanguage}>*/}
            {/*    <IcGlobe24 /> {currentLang.key}*/}
            {/*  </li>*/}
            {/*</ul>*/}
            <div
              className="Landing__burger_btn"
              onClick={() => setIsActionSheetOpened(true)}
            >
              <IcMenu28 />
            </div>
          </div>
        </div>
      </div>
      <div className="Landing__main">
        <div className="Landing__wrapper __welcome">
          <div className="Landing__main__welcome Landing__content">
            <div className="Loading__main__welcome_left">
              <h2>{t('landing_welcome_header')}</h2>
              <p>{t('landing_welcome_text')}</p>
              <div className="Loading__main__welcome_btns">
                <Button onClick={() => openAuthPopover(AuthPopoverState.REGISTER)}>
                  {t('landing_join')}
                </Button>
                <Button
                  mode="quinary"
                  onClick={() => openAuthPopover(AuthPopoverState.LOGIN)}
                >
                  {t('landing_login')}
                </Button>
              </div>
            </div>
            <div className="Loading__main__welcome_right">
              <LottieIcon name="logo" size={320} loop={true} />
            </div>
          </div>
        </div>
        <div className="Landing__wrapper __choose">
          <div className="Landing__main__choose Landing__content">
            {/*{currentLang.key !== LanguageTags.id && (*/}
            {/*  <>*/}
            {/*    <h2>{t('landing_choose_header')}</h2>*/}
            {/*    <Toggle*/}
            {/*      items={toggleItems}*/}
            {/*      value={contentType}*/}
            {/*      onClick={(id) => handleChangeContentType(id as ContentType)}*/}
            {/*    />*/}
            {/*  </>*/}
            {/*)}*/}
            <p>
              {contentType === ContentType.EROTIC
                ? t('landing_choose_text_erotic')
                : t('landing_choose_text_media')}
            </p>
            <div className="Landing__features">
              <FeatureCards cards={featureCards} contentType={contentType} />
              {/*<div className="Landing__card _logo">*/}
              {/*  <img src={TonLogo} alt="logo" />*/}
              {/*  <div className="Landing__card_content">*/}
              {/*    <h4>{t('landing_feature_title_earn')}</h4>*/}
              {/*    <p>{t('landing_feature_description_earn')}</p>*/}
              {/*    <a target="_blank" href="https://ton.org/toncoin" rel="noreferrer">*/}
              {/*      {t('landing_anchor_toncoin_website')} &gt;*/}
              {/*    </a>*/}
              {/*    <a*/}
              {/*      target="_blank"*/}
              {/*      href="https://coinmarketcap.com/ru/currencies/toncoin"*/}
              {/*      rel="noreferrer"*/}
              {/*    >*/}
              {/*      {t('landing_anchor_toncoin_price')} &gt;*/}
              {/*    </a>*/}
              {/*  </div>*/}
              {/*</div>*/}
            </div>
          </div>
        </div>
        <div
          className={`Landing__wrapper __start ${
            contentType === ContentType.EROTIC ? '_erotic' : '_media'
          }`}
        >
          <Image
            src={require('$assets/landingPhotos/photosAdultLeft.png').default}
            webp={require('$assets/landingPhotos/photosAdultLeft.webp').default}
            alt=""
            className={`Landing__photos _left ${
              contentType === ContentType.EROTIC ? '_active' : ''
            }`}
          />
          <Image
            src={require('$assets/landingPhotos/photosDefaultLeft.png').default}
            webp={require('$assets/landingPhotos/photosDefaultLeft.webp').default}
            alt=""
            className={`Landing__photos _left ${
              contentType !== ContentType.EROTIC ? '_active' : ''
            }`}
          />
          <Image
            src={require('$assets/landingPhotos/photosAdultRight.png').default}
            webp={require('$assets/landingPhotos/photosAdultRight.webp').default}
            alt=""
            className={`Landing__photos _right ${
              contentType === ContentType.EROTIC ? '_active' : ''
            }`}
          />
          <Image
            src={require('$assets/landingPhotos/photosDefaultRight.png').default}
            webp={require('$assets/landingPhotos/photosDefaultRight.webp').default}
            alt=""
            className={`Landing__photos _right ${
              contentType !== ContentType.EROTIC ? '_active' : ''
            }`}
          />
          <div className="Landing__main__start Landing__content">
            {/*{currentLang.key !== LanguageTags.id && (*/}
            {/*  <Toggle*/}
            {/*    items={[*/}
            {/*      {*/}
            {/*        ...toggleItems[0],*/}
            {/*        backgroundColor: '#fff',*/}
            {/*      },*/}
            {/*      {*/}
            {/*        ...toggleItems[1],*/}
            {/*        backgroundColor: '#fff',*/}
            {/*      },*/}
            {/*    ]}*/}
            {/*    value={contentType}*/}
            {/*    onClick={(id) => handleChangeContentType(id as ContentType)}*/}
            {/*  />*/}
            {/*)}*/}
            <h3>{t('landing_start_header')}</h3>
            <p>{t('landing_start_text')}</p>
            <Button
              mode="inverse"
              onClick={() => openAuthPopover(AuthPopoverState.REGISTER)}
            >
              {t('landing_create_free_account')}
            </Button>
          </div>
        </div>
      </div>
      <div className="Landing__wrapper __footer">
        <div className="Landing__main__footer Landing__content">
          <div className="Landing__logo">
            <IcDiamond />
            {window.isMedia ? 'TonPlace.io' : 'Ton.Place'}
          </div>
          <ul className="Landing__footer__content">
            <li>
              <a href="/anti_trafficking.html" target="_blank" rel="noreferrer">
                ANTI-TRAFFICKING STATEMENT
              </a>
            </li>
            <li>
              <a href={getDocumentHref('terms')} target="_blank" rel="noreferrer">
                {t('landing_terms_and_conditions')}
              </a>
            </li>
            <li>
              <a href="/privacy.html" target="_blank">
                {t('landing_privacy_policy')}
              </a>
            </li>
            <li>
              <a
                href="https://wiki.ton.place/oficialnye-boty"
                target="_blank"
                rel="noreferrer"
              >
                {t('landing_support')}
              </a>
            </li>
          </ul>
        </div>
        <div className="Landing__company">
          DevTech Innovations Ltd (Reg. No. 239946, address: House Of Francis, Room 303,
          Ile Du Port, Mahe, Seychelles)
        </div>
      </div>
      <ActionSheet
        isOpened={isActionSheetOpened}
        options={actions}
        onChange={handleActionClick}
        className="Landing__actionSheet"
      />
      <AuthPopoverSimple />
    </ScrollView>
  );
};

export default Landing;
